import { useMemo } from "react";
import {
  useAllergySection,
  useFamilyHistorySection,
  useMedicalHistorySection,
  useMedicationSection,
  useSocialHistorySection,
  useSurgicalHistorySection,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import { Form2Payload } from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import { useRosPeTemplateList } from "@/queries/encounter/use-ros-pe-template-list";
import { useQueryParams } from "@/hooks/use-query-param";
import { getXtenantId } from "@/utils/utils";
import {
  INITIAL_F2F_SECTIONS,
  LIBRARY_TEMPLATE_SECTIONS,
  ROS_PE_PAGE_SIZE,
} from "../initial-f2f-constant";
import {
  INITIAL_F2F_HIDDEN_SECTION_KEYS,
  INITIAL_F2F_SECTION_TITLE_OVERRIDES,
} from "../initial-f2f-form-options-constant";
import {
  ChartBackedAnswers,
  isRequiredSectionKey,
  isSectionAnswered,
} from "../initial-f2f-required-utils";
import { RequiredSectionItem, RequiredSections } from "../initial-f2f-types";
import { filterVisibleSections } from "../initial-f2f-utils";

/** Which of the note's visible sections are required and which are already answered. */
export const useRequiredSections = (values: Form2Payload): RequiredSections => {
  const { formType, noteContent, chiefComplaint } = values;
  const appointmentId = useQueryParams("appointmentid") ?? undefined;
  const xTENANTID = getXtenantId();

  const medical = useMedicalHistorySection();
  const surgical = useSurgicalHistorySection();
  const family = useFamilyHistorySection();
  const allergy = useAllergySection();
  const medication = useMedicationSection(true);
  const social = useSocialHistorySection();
  const ros = useRosPeTemplateList({
    pagination: { page: 0, pageSize: ROS_PE_PAGE_SIZE },
    gridSorting: [],
    archive: false,
    status: true,
    templateType:
      LIBRARY_TEMPLATE_SECTIONS.reviewOfSystems.encounterTemplateType,
    appointmentUuid: appointmentId,
    xTENANTID,
  });
  const pe = useRosPeTemplateList({
    pagination: { page: 0, pageSize: ROS_PE_PAGE_SIZE },
    gridSorting: [],
    archive: false,
    status: true,
    templateType: LIBRARY_TEMPLATE_SECTIONS.physicalExam.encounterTemplateType,
    appointmentUuid: appointmentId,
    xTENANTID,
  });

  const chart: ChartBackedAnswers = useMemo(
    () => ({
      medicalHistory: medical.rows.length > 0,
      surgicalHistory: surgical.rows.length > 0,
      familyHistory: family.rows.length > 0,
      allergy: allergy.rows.some(
        (row) => !!row.allergy.trim() && !!row.severity
      ),
      medication: medication.rows.some(
        (row) => !!row.medicineName.trim() && !!row.startDate
      ),
      // Social history answers live in the chart store, not in the note content.
      socialHistory: Object.values(social.store).some((value) =>
        Array.isArray(value.selected)
          ? value.selected.length > 0
          : !!value.selected
      ),
      reviewOfSystems: (ros.data?.data?.content?.length ?? 0) > 0,
      physicalExam: (pe.data?.data?.content?.length ?? 0) > 0,
    }),
    [
      medical.rows,
      surgical.rows,
      family.rows,
      allergy.rows,
      medication.rows,
      social.store,
      ros.data,
      pe.data,
    ]
  );

  return useMemo(() => {
    const visible = filterVisibleSections(
      INITIAL_F2F_SECTIONS,
      formType && INITIAL_F2F_HIDDEN_SECTION_KEYS[formType],
      formType && INITIAL_F2F_SECTION_TITLE_OVERRIDES[formType]
    );
    const items: RequiredSectionItem[] = visible.flatMap((group) =>
      group.sections.map((section) => ({
        key: section.key,
        title: section.title,
        isRequired: isRequiredSectionKey(section.key),
        isAnswered: isSectionAnswered(section.key, {
          noteContent,
          chiefComplaint: chiefComplaint ?? "",
          chart,
        }),
      }))
    );
    const missing = items.filter((it) => it.isRequired && !it.isAnswered);
    return {
      items,
      missing,
      isComplete: missing.length === 0,
    };
  }, [formType, noteContent, chiefComplaint, chart]);
};
